import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InternalApiNoteDraft } from '../../api-internal';
import { caseNoteInternalApi } from '../../apis';
import { useMe } from '../../organisation/useMe';
import { CAN_USE_NOTE_DRAFT } from '../../auth/permissions';

export const CASE_NOTE_DRAFTS_QUERY_KEY = 'caseNoteDrafts';

export const getCaseNoteDraftsQueryKey = (handle: string, organisationId: number | undefined) =>
  [CASE_NOTE_DRAFTS_QUERY_KEY, handle, organisationId] as const;

export const useQueryCaseNoteDrafts = (handle: string) => {
  const { permissions, currentOrganisation } = useMe();
  const isDraftEnabled = permissions.includes(CAN_USE_NOTE_DRAFT);

  return useQuery({
    queryKey: getCaseNoteDraftsQueryKey(handle, currentOrganisation?.id),
    queryFn: () => caseNoteInternalApi.findCaseNoteDrafts({ handle }),
    enabled: isDraftEnabled && !!handle && !!currentOrganisation?.id,
  });
};

export const useCreateCaseNoteDraft = (handle: string) => {
  return useMutation({
    mutationFn: (content: string) =>
      caseNoteInternalApi.createCaseNoteDraft({
        handle,
        internalApiCreateCaseNoteDraft: { content },
      }),
  });
};

export const useUpdateCaseNoteDraft = (handle: string) => {
  return useMutation({
    mutationFn: ({ id, content }: { id: number; content: string }) =>
      caseNoteInternalApi.updateCaseNoteDraft({
        handle,
        id,
        internalApiUpdateNoteDraft: { content },
      }),
  });
};

interface UseDeleteCaseNoteDraftParams {
  handle: string;
}

export const useDeleteCaseNoteDraft = ({ handle }: UseDeleteCaseNoteDraftParams) => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: ({ id }: { id: number }) => caseNoteInternalApi.deleteCaseNoteDraft({ handle, id }),
    onMutate: async (variables) => {
      const queryKey = getCaseNoteDraftsQueryKey(handle, currentOrganisation?.id);

      await queryClient.cancelQueries({ queryKey });

      const previousDrafts = queryClient.getQueryData<InternalApiNoteDraft[]>(queryKey);

      queryClient.setQueryData<InternalApiNoteDraft[]>(
        queryKey,
        (old) => old?.filter((draft) => draft.id !== variables.id) ?? []
      );

      return { previousDrafts, queryKey };
    },
    onError: (_, _variables, context) => {
      if (context?.previousDrafts && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousDrafts);
      }
    },
  });
};
